<script lang="ts">
  import {
    orgDotdotdot,
    orgDownload,
    orgEditMetadata,
    orgFolder,
    orgGarbage,
    orgShare,
  } from "@nrk/origo";
  import {
    collectionsPageSelectionService,
    getSelectedIds,
    getSelectedItems,
    numberOfSelectedItems,
    searchSelectionService,
  } from "state/SelectionService";
  import { isCollectionsPage, pageState, showEdit } from "state/page";
  import { slide } from "svelte/transition";
  import { getMimirSearchHits } from "../state/queries/mimir";
  import DeleteImagesButton from "./DeleteImages/DeleteImagesButton.svelte";
  import { downloadImages } from "services/download";
  import ResponsiveActionButtonsWrapper from "./ResponsiveActionButtonsWrapper.svelte";
  import { useFeatureToggle } from "services/configCatService";
  import AddToCollectionDialog from "./Collections/AddToCollectionDialog.svelte";
  import RemoveFromCollectionDialog from "./Collections/RemoveFromCollectionDialog.svelte";

  $: searchHits = getMimirSearchHits();
  $: selectionService = isCollectionsPage($pageState)
    ? collectionsPageSelectionService
    : searchSelectionService;
  $: state = $selectionService;
  $: activeSearchSource = $pageState.type === "SEARCH" && $pageState.activeSearchResultSource;

  let downloadingFiles = false;
  $: folderId = $pageState.type === "SEARCH" && $pageState.searchParams?.filter?.folderId;

  async function handleDownload() {
    downloadingFiles = true;
    try {
      await downloadImages(selectedIds);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      downloadingFiles = false;
    }
  }

  $: selectionSize = numberOfSelectedItems($selectionService);
  $: selectedIds = getSelectedIds($selectionService);
  $: selectedItems = getSelectedItems(selectedIds, $searchHits);

  let deleteDialogIsOpen = false; // bound to DeleteImagesButton's deleteDialogIsOpen state

  //Small screen functionallity
  let windowWidth;
  let showBurgerMenu = false;
  const breakPoint = 768;
  $: isMobile = windowWidth <= breakPoint;

  function toggleBurgerMenu() {
    showBurgerMenu = !showBurgerMenu;
  }
</script>

<!--
  Hadde vi vært på svelte 5 ville jeg løst responsiviteten litt anderledes:
  Knappene i en egen snippet
  {#snippet buttons()}
    <button
        on:click={handleDownload}
        class="org-button"
        aria-busy={downloadingFiles}
        disabled={downloadingFiles || activeSearchSource === "Ntb"}
        >{@html orgDownload} Last ned
      </button>
      <button
        class="org-button org-button--hidden-small org-button--icon-left"
        disabled={activeSearchSource === "Ntb"}
        on:click|preventDefault|stopPropagation={() => showEdit($selectedIds)}
      >
        {@html orgEditMetadata} Rediger metadata
      </button>
      <DeleteImagesButton {selectedItems} />
  {/snippet}

  Slettet <ResponsiveActionButtonsWrapper> og hatt responsivitet kondisjonene i denne filen
  {#if isMobile}
    {#if isOpen}
      <div class="burger-menu" transition:slide={{ duration: 200 }}>
        {@render buttons()}
      </div>
    {/if}
  {:else}
    {@render buttons()}
  {/if}
-->

<svelte:window bind:innerWidth={windowWidth} />

{#if state.matches("ItemsSelected") || deleteDialogIsOpen}
  <div class="footer" transition:slide|local={{ duration: 200 }}>
    <span class="selected-text">
      {selectionSize}
      {isCollectionsPage($pageState)
        ? `samling${selectionSize > 1 ? "er" : ""}`
        : `bilde${selectionSize > 1 ? "r" : ""}`} valgt
    </span>
    {#if isMobile}
      <button on:click={toggleBurgerMenu} class="org-button">
        {@html orgDotdotdot}
      </button>
    {/if}
    <ResponsiveActionButtonsWrapper {isMobile} isOpen={showBurgerMenu}>
      {#if isCollectionsPage($pageState)}
        <button
          class="org-button"
          data-for="edit-collection-dialog"
          disabled={selectedIds.length !== 1}
          >{@html orgEditMetadata} Endre samling
        </button>
        <button class="org-button" disabled={true} data-for="share-collection-dialog">
          {@html orgShare} Del
        </button>
        <button
          class="org-button"
          disabled={selectedIds.length > 1}
          data-for="delete-collection-dialog"
        >
          {@html orgGarbage} Slett
        </button>
      {:else}
        {#if folderId}
          <button class="org-button" data-for="remove-from-collection-dialog"
            >{@html orgFolder} Fjern fra samling
          </button>
        {/if}
        <button class="org-button" data-for="add-to-collection-dialog"
          >{@html orgFolder} Legg til i samling
        </button>
        <button
          on:click={handleDownload}
          class="org-button"
          aria-busy={downloadingFiles}
          disabled={downloadingFiles || activeSearchSource === "Ntb"}
          >{@html orgDownload} Last ned
        </button>
        <button
          class="org-button org-button--hidden-small org-button--icon-left"
          disabled={activeSearchSource === "Ntb"}
          on:click|preventDefault|stopPropagation={() => showEdit(selectedIds)}
        >
          {@html orgEditMetadata} Rediger metadata
        </button>
        <DeleteImagesButton {selectedItems} bind:deleteDialogIsOpen />
      {/if}
    </ResponsiveActionButtonsWrapper>
  </div>
  <AddToCollectionDialog selectedImages={selectedIds} isNtb={activeSearchSource === "Ntb"} />
  {#if folderId}
    <RemoveFromCollectionDialog selectedImages={selectedIds} {folderId} />
  {/if}
{/if}

<style>
  .footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: var(--color-surface-secondary);
    height: 50px;
    width: 100%;
    position: absolute;
    bottom: 0px;
  }

  .selected-text {
    font-style: italic;
    margin-right: 10px;
  }
</style>
